import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../../components/TitleBar';
import { GET_LOCATION_SUMMARY } from '../queries';
import { Footer } from '../../../components/common/Footer';
import TableV3 from '../../../components/generic/Table/TableV3';
import { Button } from '../../../components';
import { ColumnsType } from 'antd/es/table';
import { tableSortingMethods } from '../../../utils';
import FinancialData from '../../../components/common/FinancialData';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import QueryErrorBoundary from '../../../components/error-handling';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../components/common/TitleUpdater';

interface SuppliersDependencies {
  locationName: string;
  suppliersMapped: number;
  totalRevenue: number;
  uuid: string;
}

const SupplierDependencies = () => {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_LOCATION_SUMMARY);

  const { selectedTags, isLocation } = useUserContext();

  const suppliersData = data?.findAll || [];

  const filteredLocations = getFilteredTags({
    data: suppliersData,
    selectedTags,
    condition: isLocation,
    lookUpKey: 'locationUuid',
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      align: 'left',
      width: 300,
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Annual Revenue Supported',
      dataIndex: 'supportedRevenue',
      key: 'supportedRevenue',
      align: 'right',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.number(a.supportedRevenue, b.supportedRevenue),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: 'Suppliers Mapped',
      dataIndex: 'suppliersCount',
      key: 'suppliersCount',
      align: 'right',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.number(a.suppliersCount, b.suppliersCount),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
  ];

  const extendActions = (record: any) => {
    return (
      <>
        <Button
          type="primary-rounded"
          className="text-sm"
          onClick={() =>
            navigate(
              `/suppliers-dependencies/${record.locationUuid}?locationName=${record.locationName}`,
            )
          }
        >
          Map Suppliers
        </Button>
        <Button
          type="primary-rounded"
          className="text-sm min-w-[140px]"
          onClick={() =>
            navigate(
              `/suppliers-dependencies/${record.locationUuid}/summary?locationName=${record.locationName}`,
            )
          }
        >
          Show Summary
        </Button>
      </>
    );
  };

  return (
    <>
      <TitleUpdater title={`Suppliers Dependency Mapping`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={'Suppliers // Location Selection'}
        brief="Edit the supplier dependencies for any location listed below. If a location is not listed here, it means that the location has not been mapped in the dependency mapping workflow"
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Location Selection"
          columns={columns}
          data={filteredLocations}
          loading={loading}
          extendActions={extendActions}
          customStyles="custom-table--dependency-mapping"
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/exposures'} />
    </>
  );
};

export default SupplierDependencies;
